import React, { useCallback, useMemo } from "react";
import { Field, Form } from "react-final-form";
import classNames from "classnames";
import { FinalFormMutators, resetFieldsTouchedMutator } from "utils/final-form-mutators";
import { isNotEmpty } from "utils/validate";
import { checkPressedEnter } from "utils/keyboard";
import { findDictionaryItemByValue, resultElementParameterType } from "shared/rsDictionary";
import { isFormValid } from "shared/validators";
import ScrollBar from "components/ScrollBar";
import { SCROLL_BAR_MAX_HEIGHT } from "components/Dialog";
import RecipeParameterFormField from "./components/RecipeParameterFormField";
import CellModelParameterFormField from "./components/CellModelParameterFormField";
import InsightParameterFormField from "./components/InsightParameterFormField";
import ProcessVariableFormField from "./components/ProcessVariableFormField";
import { ResultElementParameterType } from "store/rs-workspace/rs-workspace.types";
import { isProcessVariableSubmitDisabled } from "store/rs-workspace/utils/results.utils";
import ResultDimensionDialog from "../../../ResultDimensionDialog";
import * as styles from "./styles.module.scss";
export var FormField;
(function (FormField) {
    FormField["TYPE"] = "type";
    FormField["NAME"] = "name";
    FormField["UNITS"] = "units";
    // Insight
    FormField["INSIGHT_ID"] = "insightId";
    FormField["INPUT_SET_ID"] = "inputSetId";
    FormField["OUTPUT"] = "output";
    // Variable
    FormField["PARAMETER"] = "parameter";
    FormField["VARIABLE_TYPE"] = "variableType";
    FormField["CUSTOM_VARIABLE_ID"] = "customVariableId";
    FormField["RECIPE_ID"] = "recipeId";
    FormField["CUT"] = "cut";
})(FormField || (FormField = {}));
const ResultElementParameterSelect = ({ title, allowedTypes, selectedParameters, noRefinedCellModels, allowMultipleUnits, processVariableRecipeIds, customVariablesWithRecipeName, initialValues, onSubmit, onClose, restrictions, classes, }) => {
    const selectedId = useMemo(() => {
        switch (initialValues?.type) {
            case ResultElementParameterType.RECIPE:
            case ResultElementParameterType.CELL_MODEL:
            case ResultElementParameterType.ALTERNATIVE_FLOW:
                return initialValues.name;
            case ResultElementParameterType.INSIGHT:
                return initialValues.insightId;
            case ResultElementParameterType.PROCESS_VARIABLE:
                return initialValues.parameter?.type;
            default:
                return;
        }
    }, [initialValues]);
    const getSubmitDisabled = useCallback((parameter) => {
        switch (parameter?.type) {
            case ResultElementParameterType.RECIPE:
            case ResultElementParameterType.CELL_MODEL:
            case ResultElementParameterType.ALTERNATIVE_FLOW:
                return !isNotEmpty(parameter.name, true);
            case ResultElementParameterType.PROCESS_VARIABLE:
                return isProcessVariableSubmitDisabled(parameter.parameter);
            case ResultElementParameterType.INSIGHT:
                return [parameter.insightId, parameter.inputSetId, parameter.output?.id].some((item) => !isNotEmpty(item, true));
            default:
                return true;
        }
    }, []);
    const renderTypeItem = useCallback((type, form) => type !== ResultElementParameterType.ALTERNATIVE_FLOW && (React.createElement(Field, { name: FormField.TYPE, key: type }, ({ input: { value } }) => (React.createElement("div", { tabIndex: 0, onClick: () => {
            if (type !== form.getFieldState(FormField.TYPE)?.value) {
                form.restart({ type });
            }
        }, onKeyDown: (e) => {
            if (checkPressedEnter(e) && type !== form.getFieldState(FormField.TYPE)?.value) {
                form.restart({ type });
            }
        }, className: classNames(styles.type, {
            [styles.active]: type === value ||
                (type === ResultElementParameterType.RECIPE &&
                    value === ResultElementParameterType.ALTERNATIVE_FLOW),
        }) }, findDictionaryItemByValue(resultElementParameterType, type)?.label)))), []);
    const renderValuesFieldsBasedOnType = useCallback((type) => {
        switch (type) {
            case ResultElementParameterType.RECIPE:
            case ResultElementParameterType.ALTERNATIVE_FLOW:
                return (React.createElement(RecipeParameterFormField, { selectedParameters: selectedParameters, restrictions: type === ResultElementParameterType.ALTERNATIVE_FLOW
                        ? restrictions?.AlternativeFlow
                        : restrictions?.Recipe, allowMultipleUnits: allowMultipleUnits }));
            case ResultElementParameterType.CELL_MODEL:
                return (React.createElement(CellModelParameterFormField, { noRefinedCellModels: noRefinedCellModels, allowMultipleUnits: allowMultipleUnits, selectedParameters: selectedParameters, restrictions: restrictions?.CellModel }));
            case ResultElementParameterType.INSIGHT:
                return (React.createElement(InsightParameterFormField, { selectedParameters: selectedParameters, restrictions: restrictions?.Insight }));
            case ResultElementParameterType.PROCESS_VARIABLE:
                return (React.createElement(ProcessVariableFormField, { processVariableRecipeIds: processVariableRecipeIds, customVariablesWithRecipeName: customVariablesWithRecipeName, selectedParameters: selectedParameters, restrictions: restrictions?.ProcessVariable?.Parameter }));
        }
    }, [
        allowMultipleUnits,
        customVariablesWithRecipeName,
        noRefinedCellModels,
        processVariableRecipeIds,
        restrictions?.AlternativeFlow,
        restrictions?.CellModel,
        restrictions?.Insight,
        restrictions?.ProcessVariable?.Parameter,
        restrictions?.Recipe,
        selectedParameters,
    ]);
    return (React.createElement(Form, { onSubmit: onSubmit, initialValues: initialValues ? initialValues : { type: allowedTypes[0] }, validateOnBlur: true, mutators: { [FinalFormMutators.resetFieldsTouchedMutator]: resetFieldsTouchedMutator } }, ({ handleSubmit, form, initialValues, values, ...renderProps }) => (React.createElement(ResultDimensionDialog, { title: title, onClose: onClose, selectedId: selectedId, onClosed: () => form.reset(initialValues), onSubmit: handleSubmit, classes: { actions: styles.dialogActions, target: classNames(styles.target, classes?.target) }, submitDisabled: getSubmitDisabled(values) || !isFormValid(renderProps), placement: "bottom-start" },
        React.createElement(ScrollBar, { autoHeight: true, autoHeightMax: SCROLL_BAR_MAX_HEIGHT },
            React.createElement("div", { className: classNames(styles.root, classes?.root) },
                React.createElement("div", { className: styles.typeList }, allowedTypes.map((i) => renderTypeItem(i, form))),
                React.createElement("div", { className: styles.values }, renderValuesFieldsBasedOnType(values.type))))))));
};
export default ResultElementParameterSelect;
